import React, { useState, useEffect, useCallback } from "react";
import {
	Card,
	Col,
	Row,
	Typography,
	Select,
	Button,
	Form,
	Input,
	message,
	Space,
	Tag,
	Divider,
	Tooltip,
	Modal,
	ColorPicker,
	Spin,
	Alert,
	theme,
	notification,
} from "antd";
import {
	SaveOutlined,
	EyeOutlined,
	DeleteOutlined,
	EditOutlined,
	HighlightOutlined,
	LoadingOutlined,
	BookOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	LeftOutlined,
	RightOutlined,
	ArrowLeftOutlined,
	DoubleRightOutlined,
	DoubleLeftOutlined,
	SettingOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router";
import {
	requestSetQuranAnnotation,
	requestShowQuranAnnotation,
} from "@/services/quran.contributor.service";
import { PiKeyboard } from "react-icons/pi";

const { Title, Text } = Typography;
const { Option, OptGroup } = Select;
const { TextArea } = Input;

const ContributorAnnotationForm = () => {
	// Get c and v from URL params
	const { c, v } = useParams();
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const { token } = theme.useToken();
	const { borderRadius, colorText, colorBgContainer, colorBgBase } = token;

	const [annotation, setAnnotation] = useState();

	// State for Quran data
	const [quranData, setQuranData] = useState([]);
	const [filteredChapters, setFilteredChapters] = useState([]);
	const [selectedChapter, setSelectedChapter] = useState(null);
	const [selectedVerse, setSelectedVerse] = useState(null);
	const [verseText, setVerseText] = useState("");
	const [loading, setLoading] = useState(false);

	// State for text selection and segments
	const [segmentForm] = Form.useForm();
	const [selectedText, setSelectedText] = useState("");
	const [selectedTextIndices, setSelectedTextIndices] = useState({ start: -1, end: -1 });
	const [segments, setSegments] = useState([]);
	const [isSegmentModalVisible, setIsSegmentModalVisible] = useState(false);
	const [currentSegment, setCurrentSegment] = useState(null);
	const [editingSegment, setEditingSegment] = useState(null);
	const [isEditMode, setIsEditMode] = useState(false);

	// Keyboard shortcuts state
	const [keyboardShortcuts, setKeyboardShortcuts] = useState({});
	const [isShortcutConfigVisible, setIsShortcutConfigVisible] = useState(false);
	const [shortcutForm] = Form.useForm();

	// Tajweed rules with codes and display names
	// Predefined colors for tajweed rule categories
	const tajweedColors = {
		nun_tanwin: "#52c41a", // Green
		mim_sakinah: "#1890ff", // Blue
		mad: "#fa541c", // Orange
		qalqalah: "#722ed1", // Purple
		ra: "#eb2f96", // Pink
		lam: "#13c2c2", // Cyan
		alif_lam: "#096dd9", // Deep Blue
		sifat: "#faad14", // Gold
		waqf: "#f5222d", // Red
		other: "#8c8c8c", // Gray
	};

	const tajweedRules = [
		// Nun Sakinah & Tanwin Rules
		{
			code: "ikhfa_haqiqi",
			name: "Ikhfa Haqiqi",
			category: "nun_tanwin",
			color: tajweedColors.nun_tanwin,
		},
		{
			code: "idgham_bighunnah",
			name: "Idgham Bighunnah",
			category: "nun_tanwin",
			color: tajweedColors.nun_tanwin,
		},
		{
			code: "idgham_bilaghunnah",
			name: "Idgham Bilaghunnah",
			category: "nun_tanwin",
			color: tajweedColors.nun_tanwin,
		},
		{ code: "iqlab", name: "Iqlab", category: "nun_tanwin", color: tajweedColors.nun_tanwin },
		{
			code: "izhar_halqi",
			name: "Izhar Halqi",
			category: "nun_tanwin",
			color: tajweedColors.nun_tanwin,
		},
		{
			code: "izhar_wajib",
			name: "Izhar Wajib",
			category: "nun_tanwin",
			color: tajweedColors.nun_tanwin,
		},

		// Mim Sakinah Rules
		{
			code: "ikhfa_syafawi",
			name: "Ikhfa Syafawi",
			category: "mim_sakinah",
			color: tajweedColors.mim_sakinah,
		},
		{
			code: "idgham_mitslain",
			name: "Idgham Mitslain",
			category: "mim_sakinah",
			color: tajweedColors.mim_sakinah,
		},
		{
			code: "izhar_syafawi",
			name: "Izhar Syafawi",
			category: "mim_sakinah",
			color: tajweedColors.mim_sakinah,
		},

		// Mad Rules
		{ code: "mad_thabii", name: "Mad Thobi'i", category: "mad", color: tajweedColors.mad },
		{
			code: "mad_wajib_muttasil",
			name: "Mad Wajib Muttasil",
			category: "mad",
			color: tajweedColors.mad,
		},
		{
			code: "mad_jaiz_munfasil",
			name: "Mad Jaiz Munfasil",
			category: "mad",
			color: tajweedColors.mad,
		},
		{
			code: "mad_arid_lissukun",
			name: "Mad Arid Lissukun",
			category: "mad",
			color: tajweedColors.mad,
		},
		{ code: "mad_lin", name: "Mad Lin", category: "mad", color: tajweedColors.mad },
		{ code: "mad_iwad", name: "Mad Iwad", category: "mad", color: tajweedColors.mad },
		{
			code: "mad_silah_qoshirah",
			name: "Mad Silah Qoshirah",
			category: "mad",
			color: tajweedColors.mad,
		},
		{
			code: "mad_silah_thowilah",
			name: "Mad Silah Thowilah",
			category: "mad",
			color: tajweedColors.mad,
		},
		{ code: "mad_badal", name: "Mad Badal", category: "mad", color: tajweedColors.mad },
		{
			code: "mad_lazim_mutsaqqal_kilmi",
			name: "Mad Lazim Mutsaqqal Kilmi",
			category: "mad",
			color: tajweedColors.mad,
		},
		{
			code: "mad_lazim_mukhaffaf_kilmi",
			name: "Mad Lazim Mukhaffaf Kilmi",
			category: "mad",
			color: tajweedColors.mad,
		},
		{
			code: "mad_lazim_mutsaqqal_harfi",
			name: "Mad Lazim Mutsaqqal Harfi",
			category: "mad",
			color: tajweedColors.mad,
		},
		{
			code: "mad_lazim_mukhaffaf_harfi",
			name: "Mad Lazim Mukhaffaf Harfi",
			category: "mad",
			color: tajweedColors.mad,
		},

		// Qalqalah Rules
		{
			code: "qalqalah_sughra",
			name: "Qalqalah Sughra",
			category: "qalqalah",
			color: tajweedColors.qalqalah,
		},
		{
			code: "qalqalah_wusta",
			name: "Qalqalah Wusta",
			category: "qalqalah",
			color: tajweedColors.qalqalah,
		},
		{
			code: "qalqalah_kubra",
			name: "Qalqalah Kubra",
			category: "qalqalah",
			color: tajweedColors.qalqalah,
		},

		// Alif Lam Rules
		{
			code: "alif_lam_syamsiyyah",
			name: "Alif Lam Syamsiyyah",
			category: "alif_lam",
			color: tajweedColors.alif_lam,
		},
		{
			code: "alif_lam_qamariyyah",
			name: "Alif Lam Qamariyyah",
			category: "alif_lam",
			color: tajweedColors.alif_lam,
		},

		// Other Rules
		{ code: "ghunnah", name: "Ghunnah", category: "other", color: tajweedColors.other },
		{ code: "ra_tafkhim", name: "Ra' Tafkhim", category: "ra", color: tajweedColors.ra },
		{ code: "ra_tarqiq", name: "Ra' Tarqiq", category: "ra", color: tajweedColors.ra },
		{ code: "lam_tafkhim", name: "Lam Tafkhim", category: "lam", color: tajweedColors.lam },
		{ code: "lam_tarqiq", name: "Lam Tarqiq", category: "lam", color: tajweedColors.lam },
		{ code: "tafkhim", name: "Tafkhim", category: "sifat", color: tajweedColors.sifat },
		{ code: "tarqiq", name: "Tarqiq", category: "sifat", color: tajweedColors.sifat },
		{ code: "waqf_tamm", name: "Waqf Tamm", category: "waqf", color: tajweedColors.waqf },
		{ code: "waqf_kafi", name: "Waqf Kafi", category: "waqf", color: tajweedColors.waqf },
		{ code: "waqf_hasan", name: "Waqf Hasan", category: "waqf", color: tajweedColors.waqf },
		{ code: "waqf_qabih", name: "Waqf Qabih", category: "waqf", color: tajweedColors.waqf },
	];

	// Default keyboard shortcuts for tajweed rules
	const defaultKeyboardShortcuts = {
		1: "mad_thabii",
		2: "mad_wajib_muttasil",
		3: "mad_jaiz_munfasil",
		4: "mad_arid_lissukun",
		5: "qalqalah_sughra",
		6: "qalqalah_kubra",
		7: "ikhfa_haqiqi",
		8: "idgham_bighunnah",
		9: "idgham_bilaghunnah",
		0: "iqlab",
		q: "izhar_halqi",
		w: "ikhfa_syafawi",
		e: "idgham_mitslain",
		r: "izhar_syafawi",
		t: "alif_lam_syamsiyyah",
		y: "alif_lam_qamariyyah",
		u: "ra_tafkhim",
		i: "ra_tarqiq",
		o: "lam_tafkhim",
		p: "lam_tarqiq",
	};

	// Load keyboard shortcuts from localStorage
	const loadKeyboardShortcuts = () => {
		try {
			const saved = localStorage.getItem("tajweed_keyboard_shortcuts");
			return saved ? JSON.parse(saved) : defaultKeyboardShortcuts;
		} catch (error) {
			console.error("Error loading keyboard shortcuts:", error);
			return defaultKeyboardShortcuts;
		}
	};

	// Save keyboard shortcuts to localStorage
	const saveKeyboardShortcuts = (shortcuts) => {
		try {
			localStorage.setItem("tajweed_keyboard_shortcuts", JSON.stringify(shortcuts));
			setKeyboardShortcuts(shortcuts);
		} catch (error) {
			console.error("Error saving keyboard shortcuts:", error);
			message.error("Gagal menyimpan shortcut keyboard");
		}
	};

	// Initialize keyboard shortcuts
	useEffect(() => {
		const shortcuts = loadKeyboardShortcuts();
		setKeyboardShortcuts(shortcuts);
	}, []);

	// Handle keyboard shortcuts
	const handleKeyboardShortcut = useCallback(
		(event) => {
			// Only handle shortcuts when modal is visible and there's selected text
			if (!isSegmentModalVisible || !selectedText) return;

			// Prevent shortcuts when typing in input fields
			if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA") return;

			const key = event.key.toLowerCase();
			const ruleCode = keyboardShortcuts[key];

			if (ruleCode && tajweedRules.find((rule) => rule.code === ruleCode)) {
				event.preventDefault();
				segmentForm.setFieldsValue({ rule: ruleCode });
				message.success(`Shortcut: ${getRuleNameFromCode(ruleCode)} (${key.toUpperCase()})`);
			}
		},
		[isSegmentModalVisible, selectedText, keyboardShortcuts, segmentForm]
	);

	// Add keyboard event listener
	useEffect(() => {
		document.addEventListener("keydown", handleKeyboardShortcut);
		return () => {
			document.removeEventListener("keydown", handleKeyboardShortcut);
		};
	}, [handleKeyboardShortcut]);

	// Reset keyboard shortcuts to default
	const resetKeyboardShortcuts = () => {
		saveKeyboardShortcuts(defaultKeyboardShortcuts);
		shortcutForm.setFieldsValue(defaultKeyboardShortcuts);
		message.success("Shortcut keyboard berhasil direset ke default");
	};

	// Handle shortcut configuration
	const handleShortcutConfig = () => {
		shortcutForm.setFieldsValue(keyboardShortcuts);
		setIsShortcutConfigVisible(true);
	};

	// Save shortcut configuration
	const handleSaveShortcuts = () => {
		shortcutForm.validateFields().then((values) => {
			// Remove empty values and duplicates
			const cleanedShortcuts = {};
			const usedKeys = new Set();
			const usedRules = new Set();

			Object.entries(values).forEach(([key, ruleCode]) => {
				if (ruleCode && !usedKeys.has(key) && !usedRules.has(ruleCode)) {
					cleanedShortcuts[key] = ruleCode;
					usedKeys.add(key);
					usedRules.add(ruleCode);
				}
			});

			saveKeyboardShortcuts(cleanedShortcuts);
			setIsShortcutConfigVisible(false);
			message.success("Shortcut keyboard berhasil disimpan");
		});
	};

	// Fetch from database
	useEffect(() => {
		if (c && v) {
			// Always reset annotation when c or v changes
			setAnnotation(null);
			setSegments([]);
			setSelectedVerse(null);
			setVerseText("");
			// Fetch from database
			requestShowQuranAnnotation(parseInt(c), parseInt(v))
				.then((response) => {
					if (response.data && response.data.data) {
						setAnnotation(response.data.data);
					}
				})
				.catch((error) => {
					if (error.response && error.response.status === 404) {
						// message.warning(`Anotasi untuk QS. ${c} : ${v} tidak ditemukan`);
					} else {
						message.error(`Gagal memuat data anotasi ${error}`);
					}
				});
		}
	}, [c, v]);

	// Pre-fill segments if annotation data is available
	useEffect(() => {
		if (annotation && verseText) {
			setSelectedText(annotation.text);
			// segment in the database only store start, end, and rule. We need to map them to our segment structure
			if (annotation.segments && Array.isArray(annotation.segments)) {
				const mappedSegments = annotation.segments.map((seg, index) => {
					// Get predefined color for the rule
					const rule = tajweedRules.find((rule) => rule.code === seg.rule);
					const ruleColor = rule ? rule.color : tajweedColors.other;
					// Get selected text from current verse then substring using start and end
					const selectedText = verseText.substring(seg.start, seg.end);

					return {
						key: `segment-${index}-${seg.start}-${seg.end}`,
						...seg,
						color: ruleColor,
						text: selectedText,
					};
				});
				setSegments(mappedSegments);
			}
		}
	}, [annotation, verseText]);

	// Get grouped tajweed rules for easier selection
	const getGroupedRules = () => {
		const grouped = tajweedRules.reduce((acc, rule) => {
			const category = rule.category;
			if (!acc[category]) {
				acc[category] = [];
			}
			acc[category].push(rule);
			return acc;
		}, {});

		const categoryLabels = {
			nun_tanwin: "Nun Sakinah & Tanwin",
			mim_sakinah: "Mim Sakinah",
			mad: "Mad (Panjang)",
			qalqalah: "Qalqalah",
			ra: "Ra'",
			lam: "Lam",
			alif_lam: "Alif Lam",
			sifat: "Sifat Huruf",
			waqf: "Waqf",
			other: "Lainnya",
		};

		return Object.keys(grouped).map((category) => ({
			category,
			label: categoryLabels[category] || category,
			rules: grouped[category],
		}));
	};

	// Get rule name from code
	const getRuleNameFromCode = (code) => {
		const rule = tajweedRules.find((r) => r.code === code);
		return rule ? rule.name : code;
	};

	// Set document title
	useEffect(() => {
		if (c && v) {
			document.title = `Islamiva Console | Tajweed QS. ${getChapterName(parseInt(c))} : ${v}`;
		} else {
			document.title = "Islamiva Console | Tambah Aturan Tajweed";
		}
	}, [c, v]);

	// Load and parse Quran data
	useEffect(() => {
		const loadQuranData = async () => {
			setLoading(true);
			try {
				// Use fetch to get the text file from public folder
				const response = await fetch("/quran.txt");
				if (!response.ok) {
					throw new Error("Failed to fetch Quran data");
				}
				const text = await response.text();
				const lines = text.split("\n").filter((line) => line.trim());

				const parsed = lines
					.map((line) => {
						const parts = line.split("|");
						if (parts.length >= 3) {
							return {
								chapter: parseInt(parts[0]),
								verse: parseInt(parts[1]),
								text: parts[2],
							};
						}
						return null;
					})
					.filter(Boolean);

				setQuranData(parsed);

				// Get unique chapters
				const chapters = [...new Set(parsed.map((item) => item.chapter))].map((chapterNum) => {
					return {
						number: chapterNum,
						name: getChapterName(chapterNum),
					};
				});

				setFilteredChapters(chapters);
			} catch (error) {
				message.error("Gagal memuat data Al-Quran");
				// Fallback: load some sample data for demonstration
				loadSampleData();
			} finally {
				setLoading(false);
			}
		};

		// Sample data for demonstration if file loading fails
		const loadSampleData = () => {
			const sampleData = [
				{ chapter: 1, verse: 1, text: "بِسْمِ اللّٰهِ الرَّحْمٰنِ الرَّحِيْمِ" },
				{ chapter: 1, verse: 2, text: "اَلْحَمْدُ لِلّٰهِ رَبِّ الْعٰلَمِيْنَۙ" },
				{ chapter: 1, verse: 3, text: "الرَّحْمٰنِ الرَّحِيْمِۙ" },
				{ chapter: 1, verse: 4, text: "مٰلِكِ يَوْمِ الدِّيْنِۗ" },
				{ chapter: 1, verse: 5, text: "اِيَّاكَ نَعْبُدُ وَاِيَّاكَ نَسْتَعِيْنُۗ" },
				{ chapter: 1, verse: 6, text: "اِهْدِنَا الصِّرَاطَ الْمُسْتَقِيْمَ ۙ" },
				{
					chapter: 1,
					verse: 7,
					text: "صِرَاطَ الَّذِيْنَ اَنْعَمْتَ عَلَيْهِمْ ەۙ غَيْرِ الْمَغْضُوْبِ عَلَيْهِمْ وَلَا الضَّاۤلِّيْنَ ࣖ",
				},
				{ chapter: 2, verse: 1, text: "الۤمّۤ ۚ" },
				{
					chapter: 2,
					verse: 2,
					text: "ذٰلِكَ الْكِتٰبُ لَا رَيْبَ ۛ فِيْهِ ۛ هُدًى لِّلْمُتَّقِيْنَۙ",
				},
			];

			setQuranData(sampleData);
			const chapters = [...new Set(sampleData.map((item) => item.chapter))].map((chapterNum) => ({
				number: chapterNum,
				name: getChapterName(chapterNum),
			}));
			setFilteredChapters(chapters);
		};

		loadQuranData();
	}, []);

	// Get chapter name (comprehensive mapping)
	const getChapterName = (chapterNumber) => {
		const chapterNames = {
			1: "Al-Fatihah",
			2: "Al-Baqarah",
			3: "Ali 'Imran",
			4: "An-Nisa'",
			5: "Al-Ma'idah",
			6: "Al-An'am",
			7: "Al-A'raf",
			8: "Al-Anfal",
			9: "At-Taubah",
			10: "Yunus",
			11: "Hud",
			12: "Yusuf",
			13: "Ar-Ra'd",
			14: "Ibrahim",
			15: "Al-Hijr",
			16: "An-Nahl",
			17: "Al-Isra'",
			18: "Al-Kahf",
			19: "Maryam",
			20: "Taha",
			21: "Al-Anbiya'",
			22: "Al-Hajj",
			23: "Al-Mu'minun",
			24: "An-Nur",
			25: "Al-Furqan",
			26: "Ash-Shu'ara'",
			27: "An-Naml",
			28: "Al-Qasas",
			29: "Al-'Ankabut",
			30: "Ar-Rum",
			31: "Luqman",
			32: "As-Sajdah",
			33: "Al-Ahzab",
			34: "Saba'",
			35: "Fatir",
			36: "Ya-Sin",
			37: "As-Saffat",
			38: "Sad",
			39: "Az-Zumar",
			40: "Ghafir",
			41: "Fussilat",
			42: "Ash-Shura",
			43: "Az-Zukhruf",
			44: "Ad-Dukhan",
			45: "Al-Jathiyah",
			46: "Al-Ahqaf",
			47: "Muhammad",
			48: "Al-Fath",
			49: "Al-Hujurat",
			50: "Qaf",
			51: "Adh-Dhariyat",
			52: "At-Tur",
			53: "An-Najm",
			54: "Al-Qamar",
			55: "Ar-Rahman",
			56: "Al-Waqi'ah",
			57: "Al-Hadid",
			58: "Al-Mujadilah",
			59: "Al-Hashr",
			60: "Al-Mumtahanah",
			61: "As-Saff",
			62: "Al-Jumu'ah",
			63: "Al-Munafiqun",
			64: "At-Taghabun",
			65: "At-Talaq",
			66: "At-Tahrim",
			67: "Al-Mulk",
			68: "Al-Qalam",
			69: "Al-Haqqah",
			70: "Al-Ma'arij",
			71: "Nuh",
			72: "Al-Jinn",
			73: "Al-Muzzammil",
			74: "Al-Muddaththir",
			75: "Al-Qiyamah",
			76: "Al-Insan",
			77: "Al-Mursalat",
			78: "An-Naba'",
			79: "An-Nazi'at",
			80: "'Abasa",
			81: "At-Takwir",
			82: "Al-Infitar",
			83: "Al-Mutaffifin",
			84: "Al-Inshiqaq",
			85: "Al-Buruj",
			86: "At-Tariq",
			87: "Al-A'la",
			88: "Al-Ghashiyah",
			89: "Al-Fajr",
			90: "Al-Balad",
			91: "Ash-Shams",
			92: "Al-Layl",
			93: "Ad-Duha",
			94: "Ash-Sharh",
			95: "At-Tin",
			96: "Al-'Alaq",
			97: "Al-Qadr",
			98: "Al-Bayyinah",
			99: "Az-Zalzalah",
			100: "Al-'Adiyat",
			101: "Al-Qari'ah",
			102: "At-Takathur",
			103: "Al-'Asr",
			104: "Al-Humazah",
			105: "Al-Fil",
			106: "Quraysh",
			107: "Al-Ma'un",
			108: "Al-Kawthar",
			109: "Al-Kafirun",
			110: "An-Nasr",
			111: "Al-Masad",
			112: "Al-Ikhlas",
			113: "Al-Falaq",
			114: "An-Nas",
		};
		return chapterNames[chapterNumber] || `Chapter ${chapterNumber}`;
	};

	// Handle chapter selection
	const handleChapterChange = (chapterNumber) => {
		setSelectedChapter(chapterNumber);
		setSelectedVerse(null);
		setVerseText("");
		setSegments([]);
		form.setFieldsValue({ verse: undefined });
	};

	// Handle verse selection
	const handleVerseChange = (verseNumber) => {
		setSelectedVerse(verseNumber);
		const verseData = quranData.find(
			(item) => item.chapter === selectedChapter && item.verse === verseNumber
		);
		if (verseData) {
			setVerseText(verseData.text);
			setSegments([]);
		}
	};

	// Get verses for selected chapter
	const getVersesForChapter = (chapterNumber) => {
		return quranData
			.filter((item) => item.chapter === chapterNumber)
			.map((item) => item.verse)
			.sort((a, b) => a - b);
	};

	// Handle URL parameters for pre-selected chapter and verse
	useEffect(() => {
		if (c && v && quranData.length > 0) {
			const chapterNum = parseInt(c);
			const verseNum = parseInt(v);

			// Check if the chapter and verse exist in our data
			const verseExists = quranData.find(
				(item) => item.chapter === chapterNum && item.verse === verseNum
			);

			if (verseExists) {
				// Set the chapter first
				setSelectedChapter(chapterNum);
				form.setFieldsValue({ chapter: chapterNum });

				// Then set the verse
				setTimeout(() => {
					setSelectedVerse(verseNum);
					setVerseText(verseExists.text);
					form.setFieldsValue({ verse: verseNum });
				}, 100); // Small delay to ensure chapter selection is processed
			} else {
				message.warning(`Chapter ${chapterNum}, Verse ${verseNum} tidak ditemukan dalam data`);
				navigate(-1);
			}
		}
	}, [c, v, quranData, form]);

	// Handle text selection with better positioning
	const handleTextSelection = (element) => {
		const selection = window.getSelection();
		const selectedTextContent = selection.toString();

		if (selectedTextContent && selectedTextContent.length > 0) {
			// Check if selected text is within the verse text
			if (verseText.includes(selectedTextContent)) {
				setSelectedText(selectedTextContent);
				setIsEditMode(false); // Reset edit mode when selecting new text
				setEditingSegment(null);
				segmentForm.resetFields();
				setIsSegmentModalVisible(true);
			} else {
				message.warning("Silakan pilih teks dari dalam verse yang ditampilkan");
			}
		}

		// Get start and end index of selection
		const range = selection.getRangeAt(0);
		const preSelectionRange = range.cloneRange();
		preSelectionRange.selectNodeContents(element);
		preSelectionRange.setEnd(range.startContainer, range.startOffset);
		const startIndex = preSelectionRange.toString().length;
		const endIndex = startIndex + range.toString().length;

		setSelectedTextIndices({ start: startIndex, end: endIndex });
	};

	// Add segment with overlap detection
	const handleAddSegment = () => {
		segmentForm
			.validateFields()
			.then((values) => {
				if (isEditMode && editingSegment) {
					// Update existing segment
					const updatedSegments = segments.map((seg) => {
						if (seg.start === editingSegment.start && seg.end === editingSegment.end) {
							// Get predefined color for the selected rule
							const selectedRule = tajweedRules.find((rule) => rule.code === values.rule);
							const ruleColor = selectedRule ? selectedRule.color : tajweedColors.other;

							return {
								...seg,
								rule: values.rule,
								color: ruleColor,
							};
						}
						return seg;
					});

					setSegments(updatedSegments);
					setIsSegmentModalVisible(false);
					setIsEditMode(false);
					setEditingSegment(null);
					setSelectedText("");
					message.success("Segment berhasil diperbarui");
					segmentForm.resetFields();
					return;
				}

				// Add new segment logic (existing code)
				if (!selectedText || selectedTextIndices.start === -1 || selectedTextIndices.end === -1) {
					return;
				}

				const startIndex = selectedTextIndices.start;
				const endIndex = selectedTextIndices.end;

				// Check for overlapping segments
				const hasOverlap = segments.some((seg) => startIndex < seg.end && endIndex > seg.start);

				if (hasOverlap) {
					message.warning(
						"Teks yang dipilih sudah memiliki segment atau tumpang tindih dengan segment lain"
					);
					return;
				}

				// Get predefined color for the selected rule
				const selectedRule = tajweedRules.find((rule) => rule.code === values.rule);
				const ruleColor = selectedRule ? selectedRule.color : tajweedColors.other;

				const newSegment = {
					rule: values.rule,
					start: startIndex,
					end: endIndex,
					text: selectedText,
					color: ruleColor,
				};

				const newSegments = [...segments, newSegment];
				// Sort segments by start index
				newSegments.sort((a, b) => a.start - b.start);
				setSegments(newSegments);
				setIsSegmentModalVisible(false);
				setSelectedText("");
				message.success("Segment berhasil ditambahkan");

				// Clear selection
				window.getSelection().removeAllRanges();
			})
			.catch((error) => {});
	};

	// Remove segment
	const handleRemoveSegment = (start) => {
		if (annotation && annotation.verified) {
			notification.error({
				message: "Segment tidak dapat dihapus",
				description: "Anotasi telah diverifikasi dan tidak dapat diubah.",
			});
			return;
		}
		setSegments(segments.filter((seg) => seg.start !== start));
		message.success("Segment berhasil dihapus");
	};

	// Edit segment
	const handleEditSegment = (segment) => {
		if (annotation && annotation.verified) {
			notification.error({
				message: "Segment tidak dapat diedit",
				description: "Anotasi telah diverifikasi dan tidak dapat diubah.",
			});
			return;
		}
		setEditingSegment(segment);
		setIsEditMode(true);
		setSelectedText(segment.text);
		segmentForm.setFieldsValue({
			rule: segment.rule,
		});
		setIsSegmentModalVisible(true);
	};

	// Render verse text with segments
	const renderSegmentedText = () => {
		if (!verseText) return null;

		let result = [];
		let currentIndex = 0;

		// Sort segments by start index
		const sortedSegments = [...segments].sort((a, b) => a.start - b.start);

		sortedSegments.forEach((segment, index) => {
			// Add text before segment
			if (currentIndex < segment.start) {
				result.push(
					<span
						key={`text-${index}`}
						style={{ color: colorText, backgroundColor: colorBgContainer }}>
						{verseText.substring(currentIndex, segment.start)}
					</span>
				);
			}

			// Add segmented text with predefined color from rule
			const ruleColor = segment.color || tajweedColors.other;

			// Create unique style for this segment - minimal interference approach
			const segmentStyle = {
				backgroundColor: ruleColor,
				color: "white",
				// Minimal styling to preserve Arabic text flow
				textDecoration: "none",
				borderRadius: "1px",
				cursor: "pointer",
				fontWeight: "inherit", // Don't change font weight
				fontSize: "inherit", // Keep original font size
				fontFamily: "inherit", // Keep original font family
				lineHeight: "inherit", // Keep original line height
				// Use box-shadow instead of border to avoid layout shifts
				boxShadow: `inset 0 0 0 1px ${ruleColor}`,
				position: "relative",
				transition: "box-shadow 0.15s ease",
				border: "none",
				outline: "none",
				userSelect: "none",
				// Preserve Arabic text rendering
				textRendering: "auto",
				direction: "inherit",
				unicodeBidi: "normal",
			};

			result.push(
				<Tooltip
					key={`segment-${segment.start}-${segment.end}`}
					title={
						<div>
							<div>
								<strong>{getRuleNameFromCode(segment.rule)}</strong>
							</div>
							<div>{segment.description}</div>
							<div style={{ marginTop: 8, fontSize: "12px", opacity: 0.8 }}>
								Klik untuk edit • Shift+Klik untuk detail
							</div>
						</div>
					}>
					<span
						style={segmentStyle}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							if (e.shiftKey) {
								// Shift+Click for detail modal
								setCurrentSegment(segment);
							} else {
								// Regular click for edit
								handleEditSegment(segment);
							}
						}}
						onMouseEnter={(e) => {
							// Subtle hover effect without layout change
							e.target.style.boxShadow = `inset 0 0 0 1px ${ruleColor}, 0 0 3px rgba(0,0,0,0.2)`;
						}}
						onMouseLeave={(e) => {
							// Reset to original box-shadow
							e.target.style.boxShadow = `inset 0 0 0 1px ${ruleColor}`;
						}}>
						{segment.text}
					</span>
				</Tooltip>
			);

			currentIndex = segment.end;
		});

		// Add remaining text
		if (currentIndex < verseText.length) {
			result.push(
				<span key="text-end" style={{ color: colorText, backgroundColor: colorBgContainer }}>
					{verseText.substring(currentIndex)}
				</span>
			);
		}

		return result;
	};

	// Save tajweed rule
	const handleSave = async (values) => {
		try {
			const payload = {
				chapter: selectedChapter,
				verse: selectedVerse,
				segments: segments,
			};

			requestSetQuranAnnotation(payload)
				.then(() => {
					if (annotation) {
						setAnnotation({
							...annotation,
							...payload,
						});
					} else {
						setAnnotation(payload);
					}
					message.success("Berhasil menyimpan aturan tajwid");
				})
				.catch((error) => {
					message.error("Gagal menyimpan aturan tajwid");
				});
		} catch (error) {
			message.error("Gagal menyimpan aturan tajwid");
		}
	};

	const handleNavigateVerse = (direction, type) => {
		if (type === "chapter") {
			if (direction === "prev") {
				if (Number(c) <= 1) {
					message.warning("Tidak ada chapter sebelumnya");
					return;
				}
				navigate(`/contributor/annotations/${Number(c) - 1}/1`);
			} else {
				// Get total verses in the next chapter
				const nextChapterNum = Number(c) + 1;
				const versesInNextChapter = getVersesForChapter(nextChapterNum);
				if (versesInNextChapter.length > 0) {
					const lastVerseNum = versesInNextChapter[versesInNextChapter.length - 1];
					navigate(`/contributor/annotations/${nextChapterNum}/1`);
				} else {
					message.warning("Tidak ada chapter berikutnya");
				}
			}
		} else {
			if (direction === "prev") {
				if (Number(v) <= 1) {
					message.warning("Tidak ada verse sebelumnya");
					return;
				}
				navigate(`/contributor/annotations/${c}/${Number(v) - 1}`);
			} else {
				// Get total verses in the current chapter
				const versesInChapter = getVersesForChapter(Number(c));
				if (Number(v) >= versesInChapter.length) {
					message.warning("Tidak ada verse berikutnya");
					return;
				}
				navigate(`/contributor/annotations/${c}/${Number(v) + 1}`);
			}
		}
	};

	return (
		<div>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Card>
						<Space direction="vertical" style={{ width: "100%" }}>
							<Space
								direction="horizontal"
								style={{ justifyContent: "space-between", width: "100%" }}>
								<Space direction="horizontal" size="small">
									<Button
										icon={<ArrowLeftOutlined />}
										onClick={() => navigate("/contributor/annotations")}
									/>
									<Title level={4} style={{ margin: 0 }}>
										{c && v
											? `Aturan Tajweed - QS. ${getChapterName(parseInt(c))} : ${v}`
											: "Tambah Aturan Tajweed"}
									</Title>
									{annotation ? (
										<Tag color={annotation && annotation.verified ? "green" : "red"}>
											{annotation && annotation.verified ? "Terverifikasi" : "Belum Terverifikasi"}
										</Tag>
									) : (
										<Tag color="warning">Belum Ada Data</Tag>
									)}
								</Space>
								{/* <Button onClick={() => navigate("/annotations")} icon={<BookOutlined />}>
									Kembali ke Daftar
								</Button> */}
								<Space direction="horizontal" size="small">
									{/* Next and Prev Verse */}
									<Button
										icon={<DoubleLeftOutlined />}
										onClick={() => handleNavigateVerse("prev", "chapter")}
									/>
									<Button
										icon={<LeftOutlined />}
										onClick={() => handleNavigateVerse("prev", "verse")}
									/>
									<Button
										icon={<RightOutlined />}
										onClick={() => handleNavigateVerse("next", "verse")}
									/>
									<Button
										icon={<DoubleRightOutlined />}
										onClick={() => handleNavigateVerse("next", "chapter")}
									/>
								</Space>
							</Space>
						</Space>
					</Card>
				</Col>

				<Col span={24}>
					<Spin spinning={loading} tip="Memuat data Al-Quran...">
						<Form form={form} layout="vertical" onFinish={handleSave}>
							{/* <Row gutter={[16, 16]}>
								<Col span={12}>
									<Form.Item
										label="Chapter"
										name="chapter"
										rules={[{ required: true, message: "Chapter harus dipilih" }]}>
										<Select
											placeholder="Pilih chapter"
											onChange={handleChapterChange}
											showSearch
											disabled={loading || (c && v)} // Disable if pre-selected from URL
											filterOption={(input, option) =>
												option.children.toLowerCase().includes(input.toLowerCase())
											}>
											{filteredChapters.map((chapter) => (
												<Option key={chapter.number} value={chapter.number}>
													{chapter.number}. {chapter.name}
												</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="Verse"
										name="verse"
										rules={[{ required: true, message: "Verse harus dipilih" }]}>
										<Select
											placeholder="Pilih verse"
											onChange={handleVerseChange}
											disabled={!selectedChapter || loading || (c && v)}>
											{selectedChapter &&
												getVersesForChapter(selectedChapter).map((verseNum) => (
													<Option key={verseNum} value={verseNum}>
														Verse {verseNum}
													</Option>
												))}
										</Select>
									</Form.Item>
								</Col>
							</Row> */}

							{!loading && verseText && (
								<>
									<Alert
										message={
											<Space>
												<span>Cara Menambahkan & Mengedit Anotasi</span>
												<Button
													size="small"
													icon={<SettingOutlined />}
													onClick={handleShortcutConfig}
													type="dashed">
													Atur Shortcut
												</Button>
											</Space>
										}
										description={
											<div>
												<div>
													• <strong>Tambah segment:</strong> Pilih/blok teks pada verse dengan
													mouse, kemudian tambahkan aturan tajweed
												</div>
												<div>
													• <strong>Edit segment:</strong> Klik pada segment yang sudah ada (teks
													berwarna) untuk mengedit
												</div>
												<div>
													• <strong>Detail segment:</strong> Shift+Klik pada segment untuk melihat
													detail
												</div>
												<div>
													• <strong>Keyboard shortcut:</strong> Tekan tombol keyboard (1-9, 0, Q-P)
													saat memilih aturan untuk cepat memilih aturan tajweed
												</div>
											</div>
										}
										type="info"
										showIcon
										style={{ marginBottom: 16 }}
									/>
									<Card
										style={{ marginBottom: 16 }}
										title={
											<Space>
												<Text strong>
													QS. {getChapterName(selectedChapter)} : {selectedVerse}
												</Text>
												<Tooltip title="Pilih teks untuk menambahkan anotasi">
													<HighlightOutlined />
												</Tooltip>
											</Space>
										}>
										<div
											style={{
												fontSize: "32px",
												lineHeight: "2.5",
												fontFamily: "Misbah, sans-serif",
												direction: "rtl",
												textAlign: "right",
												padding: "20px",
												border: "2px dashed #d9d9d9",
												borderRadius: borderRadius,
												cursor: "text",
												userSelect: "text",
												minHeight: "80px",
												backgroundColor: colorBgContainer,
												transition: "all 0.3s",
											}}
											onMouseUp={(e) => handleTextSelection(e.currentTarget)}
											onMouseEnter={(e) => {
												e.target.style.borderColor = "#1890ff";
												// e.target.style.backgroundColor = colorBgBase;
											}}
											onMouseLeave={(e) => {
												e.target.style.borderColor = "#d9d9d9";
												// e.target.style.backgroundColor = colorBgContainer;
											}}>
											{segments.length > 0 ? renderSegmentedText() : verseText}
										</div>
									</Card>

									{segments.length > 0 && (
										<Card title="Segment Tajweed" style={{ marginBottom: 16 }}>
											<Space direction="horizontal" style={{ width: "100%", flexWrap: "wrap" }}>
												{segments.map((segment) => (
													<Card
														size="small"
														key={`${c}-${v}-${segment.start}`}
														style={{
															display: "flex",
															justifyContent: "space-between",
															alignItems: "center",
														}}>
														<Space>
															<Tag color={segment.color} style={{ fontSize: "14px" }}>
																{segment.text}
															</Tag>
															<Text strong style={{ color: "#1890ff" }}>
																{getRuleNameFromCode(segment.rule)}
															</Text>
															<Text type="secondary">{segment.description}</Text>
														</Space>
														<Space>
															<Tooltip title="Edit Segmen">
																<Button
																	type="text"
																	icon={<EditOutlined />}
																	onClick={() => handleEditSegment(segment)}
																/>
															</Tooltip>
															<Button
																type="text"
																danger
																icon={<DeleteOutlined />}
																onClick={() => handleRemoveSegment(segment.start)}
															/>
														</Space>
													</Card>
												))}
											</Space>
										</Card>
									)}

									{/* Color Legend */}
									<Card title="Keterangan Warna Tajweed" style={{ marginBottom: 16 }}>
										<Row gutter={[16, 8]}>
											<Col span={8}>
												<Space>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor: tajweedColors.nun_tanwin,
															borderRadius: 3,
														}}></div>
													<Text>Nun Sakinah & Tanwin</Text>
												</Space>
											</Col>
											<Col span={8}>
												<Space>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor: tajweedColors.mim_sakinah,
															borderRadius: 3,
														}}></div>
													<Text>Mim Sakinah</Text>
												</Space>
											</Col>
											<Col span={8}>
												<Space>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor: tajweedColors.mad,
															borderRadius: 3,
														}}></div>
													<Text>Mad</Text>
												</Space>
											</Col>
											<Col span={8}>
												<Space>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor: tajweedColors.qalqalah,
															borderRadius: 3,
														}}></div>
													<Text>Qalqalah</Text>
												</Space>
											</Col>
											<Col span={8}>
												<Space>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor: tajweedColors.ra,
															borderRadius: 3,
														}}></div>
													<Text>Ra' (Tafkhim/Tarqiq)</Text>
												</Space>
											</Col>
											<Col span={8}>
												<Space>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor: tajweedColors.lam,
															borderRadius: 3,
														}}></div>
													<Text>Lam (Tafkhim/Tarqiq)</Text>
												</Space>
											</Col>
											<Col span={8}>
												<Space>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor: tajweedColors.alif_lam,
															borderRadius: 3,
														}}></div>
													<Text>Alif Lam</Text>
												</Space>
											</Col>
											<Col span={8}>
												<Space>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor: tajweedColors.sifat,
															borderRadius: 3,
														}}></div>
													<Text>Sifat Huruf</Text>
												</Space>
											</Col>
											<Col span={8}>
												<Space>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor: tajweedColors.waqf,
															borderRadius: 3,
														}}></div>
													<Text>Waqf</Text>
												</Space>
											</Col>
											<Col span={8}>
												<Space>
													<div
														style={{
															width: 16,
															height: 16,
															backgroundColor: tajweedColors.other,
															borderRadius: 3,
														}}></div>
													<Text>Lainnya</Text>
												</Space>
											</Col>
										</Row>
									</Card>
								</>
							)}

							<Form.Item>
								{/* align to right */}
								<Space size="middle" style={{ display: "flex", justifyContent: "flex-end" }}>
									<Button onClick={() => navigate("/annotations")} disabled={loading}>
										Batal
									</Button>
									<Button
										type="primary"
										htmlType="submit"
										icon={<SaveOutlined />}
										disabled={loading || !verseText || annotation?.verified}>
										{annotation?.verified ? "Terverifikasi" : "Simpan Aturan Tajwid"}
									</Button>
								</Space>
							</Form.Item>
						</Form>
					</Spin>
				</Col>
			</Row>

			{/* Segment Modal */}
			<Modal
				title={isEditMode ? "Edit Segment Tajweed" : "Tambah Segment Tajweed"}
				open={isSegmentModalVisible}
				onCancel={() => {
					setIsSegmentModalVisible(false);
					setIsEditMode(false);
					setEditingSegment(null);
					setSelectedText("");
					window.getSelection().removeAllRanges();
				}}
				footer={[
					<Button
						key="cancel"
						onClick={() => {
							setIsSegmentModalVisible(false);
							setIsEditMode(false);
							setEditingSegment(null);
							setSelectedText("");
							window.getSelection().removeAllRanges();
						}}>
						Batal
					</Button>,
					<Button key="submit" type="primary" onClick={handleAddSegment} disabled={!selectedText}>
						{isEditMode ? "Update" : "Simpan"}
					</Button>,
				]}
				width={600}>
				<Form layout="vertical" form={segmentForm}>
					<Form.Item label="Teks Terpilih">
						<Typography.Paragraph
							style={{
								fontSize: "24px",
								fontFamily: "Misbah, sans-serif",
								direction: "rtl",
								textAlign: "center",
							}}>
							{selectedText}
						</Typography.Paragraph>
					</Form.Item>

					<Form.Item
						label="Aturan Tajweed"
						name="rule"
						rules={[{ required: true, message: "Aturan harus dipilih" }]}>
						<Select
							placeholder="Pilih aturan tajweed"
							allowClear
							showSearch
							filterOption={(input, option) => {
								// Get the rule name from the option value
								const rule = tajweedRules.find((r) => r.code === option?.value);
								return (
									rule?.name?.toLowerCase().includes(input.toLowerCase()) ||
									option?.value?.toLowerCase().includes(input.toLowerCase())
								);
							}}>
							{getGroupedRules().map((group) => (
								<OptGroup key={group.category} label={group.label}>
									{group.rules.map((rule) => {
										// Find keyboard shortcut for this rule
										const shortcutKey = Object.keys(keyboardShortcuts).find(
											(key) => keyboardShortcuts[key] === rule.code
										);

										return (
											<Option key={rule.code} value={rule.code}>
												<Space
													style={{
														display: "flex",
														flexDirection: "row",
														justifyContent: "space-between",
													}}>
													<span>{rule.name}</span>
													{shortcutKey && (
														<Tag
															size="small"
															style={{
																fontFamily: "monospace",
															}}>
															{shortcutKey.toUpperCase()}
														</Tag>
													)}
												</Space>
											</Option>
										);
									})}
								</OptGroup>
							))}
						</Select>
					</Form.Item>

					{Object.keys(keyboardShortcuts).length > 0 && (
						<Form.Item>
							<Card
								size="small"
								style={{ backgroundColor: colorBgContainer, maxHeight: "200px", overflowY: "auto" }}
								title="Shortcut Keyboard Aktif">
								<Space wrap>
									{Object.entries(keyboardShortcuts).map(([key, ruleCode]) => {
										const rule = tajweedRules.find((r) => r.code === ruleCode);
										if (!rule) return null;
										return (
											<Tag key={key} color="blue" style={{ fontFamily: "monospace" }}>
												{key.toUpperCase()} → {rule.name}
											</Tag>
										);
									})}
								</Space>
							</Card>
						</Form.Item>
					)}
				</Form>
			</Modal>

			{/* Segment Details Modal */}
			{currentSegment && (
				<Modal
					title="Detail Segment"
					open={!!currentSegment}
					onCancel={() => setCurrentSegment(null)}
					footer={[
						<Button key="close" onClick={() => setCurrentSegment(null)}>
							Tutup
						</Button>,
					]}>
					<Space direction="vertical" style={{ width: "100%" }}>
						<div>
							<Text strong>Teks: </Text>
							<Tag color={currentSegment.color}>{currentSegment.text}</Tag>
						</div>
						<div>
							<Text strong>Aturan: </Text>
							<Text>{getRuleNameFromCode(currentSegment.rule)}</Text>
						</div>
						<div>
							<Text strong>Deskripsi: </Text>
							<Text>{currentSegment.description}</Text>
						</div>
					</Space>
				</Modal>
			)}

			{/* Keyboard Shortcut Configuration Modal */}
			<Modal
				title={
					<Space>
						<PiKeyboard />
						<span>Konfigurasi Shortcut Keyboard</span>
					</Space>
				}
				open={isShortcutConfigVisible}
				onCancel={() => setIsShortcutConfigVisible(false)}
				footer={[
					<Button key="reset" icon={<ReloadOutlined />} onClick={resetKeyboardShortcuts}>
						Reset Default
					</Button>,
					<Button key="cancel" onClick={() => setIsShortcutConfigVisible(false)}>
						Batal
					</Button>,
					<Button key="save" type="primary" onClick={handleSaveShortcuts}>
						Simpan
					</Button>,
				]}
				width={800}>
				<Alert
					message="Petunjuk Penggunaan"
					description={
						<div>
							<div>
								• Tekan tombol keyboard saat modal pemilihan aturan terbuka untuk memilih aturan
								dengan cepat
							</div>
							<div>• Setiap tombol hanya bisa digunakan untuk satu aturan</div>
							<div>• Kosongkan field untuk menghapus shortcut</div>
						</div>
					}
					type="info"
					showIcon
					style={{ marginBottom: 16 }}
				/>

				<Form form={shortcutForm} layout="vertical">
					<Row gutter={[16, 8]}>
						{Object.entries(defaultKeyboardShortcuts).map(([key, defaultRule]) => (
							<Col span={8} key={key}>
								<Form.Item
									label={
										<Space>
											<Tag>{key.toUpperCase()}</Tag>
											<span>→</span>
										</Space>
									}
									name={key}>
									<Select
										placeholder="Pilih aturan"
										allowClear
										showSearch
										filterOption={(input, option) =>
											option?.children?.toLowerCase().includes(input.toLowerCase())
										}>
										{tajweedRules.map((rule) => (
											<Option key={rule.code} value={rule.code}>
												{rule.name}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>
						))}
					</Row>
				</Form>
			</Modal>
		</div>
	);
};

export default ContributorAnnotationForm;
